/*
 * RetrySubscriptionForm.tsx (AbstractECommerce)
 *
 * Copyright © 2024 InstaLOD GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * Maintained by Rafael Rodrigues, 2024
 *
 * @file RetrySubscriptionForm.tsx
 * @author Rafael Rodrigues
 * @copyright 2024 InstaLOD GmbH. All rights reserved.
 * @section License
 */

import React from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { withFormik } from 'formik';
import * as Yup from 'yup';
import Form from 'react-bootstrap/Form';
import InstaInputText from '@abstract/abstractwebcommon-client/FormControl/InstaInputText';
import i18n from '../../../Services/I18n';
import FormWrapper from '@abstract/abstractwebcommon-client/FormControl/FormWrapper';
import { ISubscription } from '@abstract/abstractwebcommon-shared/interfaces/ecommerce/Subscription';

/**
 * Interface for IHandleSubmitRetrySubscriptionForm properties.
 */
interface IHandleSubmitRetrySubscriptionForm {
  transactionPrice: string /* Displays the last transaction price and holds the new transaction price to retry */;
}

/**
 * Interface for IRetrySubscriptionFormProperties properties.
 */
interface IRetrySubscriptionFormProperties {
  values: any;
  errors: any;
  handleChange: any;
  handleSubmit: (values: IHandleSubmitRetrySubscriptionForm) => void;
  isLoading: boolean;
  subscription: ISubscription;
  handleBlur: any;
  touched: any;
}

const RetrySubscriptionForm = (
  properties: IRetrySubscriptionFormProperties
) => {
  return (
    <FormWrapper
      isLoading={properties.isLoading}
      handleSubmitButton={(submitValues: IHandleSubmitRetrySubscriptionForm) =>
        properties.handleSubmit(submitValues)
      }
    >
      <Row as={Col} className="mb-3">
        {i18n.t(
          'adminSubscriptions.retry_subscription_form.form_sentence_content'
        )}
      </Row>
      <Row>
        <Form.Group as={Col} className="mb-3">
          <InstaInputText
            label={i18n.t(
              'adminSubscriptions.retry_subscription_form.last_transaction_price_field'
            )}
            labelClassName="required"
            name="transactionPrice"
            id="transactionPrice"
            isLoading={properties.isLoading}
            onChange={(e) => properties.handleChange(e)}
            onBlur={properties.handleBlur}
            touched={properties.touched.transactionPrice}
            errors={properties.errors.transactionPrice}
            value={properties.values.transactionPrice}
          />
        </Form.Group>
      </Row>
    </FormWrapper>
  );
};

/**
 * Interface for IHandleSubmitRetrySubscriptionForm properties.
 */
type IWithFormikRetrySubscriptionForm = IRetrySubscriptionFormProperties;

export default withFormik({
  mapPropsToValues: (formikProperties: IWithFormikRetrySubscriptionForm) => {
    if (
      formikProperties.subscription &&
      formikProperties.subscription.braintreeTransactions
    ) {
      // The first array here is always the last bound transaction of the current subscription.
      return {
        transactionPrice:
          formikProperties.subscription.braintreeTransactions[0].amount
      };
    } else {
      return { transactionPrice: '' };
    }
  },
  validateOnChange: false,
  validateOnMount: false,
  validationSchema: Yup.object().shape({
    transactionPrice: Yup.number()
      .typeError('Must be a number')
      .required(
        i18n.t('validation.required', {
          field: i18n.t(
            'adminSubscriptions.retry_subscription_form.last_transaction_price_field'
          )
        })
      )
  }),
  handleSubmit: (values, bag) => {
    return bag.props.handleSubmit({ ...values });
  }
})(RetrySubscriptionForm);
